import React, { useEffect, useRef } from 'react';
import OwnerVerificationConfirmModal from './components/OwnerVerificationConfirmModal/OwnerVerificationConfirmModal';
import EstateDetailStep from './components/EstateDetailStep/EstateDetailStep';
import NoticeStep from './components/NoticeStep/NoticeStep';
import RecipientInfoStep from './components/RecipientInfoStep/RecipientInfoStep';
import ExitConfirmModal from './components/ExitConfirmModal/ExitConfirmModal';
import VerificationStatusModal from './components/VerificationStatusModal/VerificationStatusModal';
import DirectShareModal from './components/DirectShareModal/DirectShareModal';
import MembershipRequiredModal from './components/MembershipRequiredModal/MembershipRequiredModal';
import useOwnerVerification, { STEP } from './hooks/useOwnerVerification';
import { toast, TOAST_POSITION } from '../../components/material/ToastPopup';
import { uploadFileToAurora } from '../../services/upload/uploadService';
import * as ovApi from './apis/ownerVerificationApi';
import { addDays, today } from './utils/date';
import { OV_SERVICE_NAME } from './constants/constants';

function OwnerVerificationWidget({ saleUuid, verificationUuid, action, onComplete, onClose }) {
  const ov = useOwnerVerification();
  const [directShareOpen, setDirectShareOpen] = React.useState(false);
  const [directShareLink, setDirectShareLink] = React.useState('');
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (saleUuid) {
      ov.startVerification(saleUuid, {
        verificationUuid,
        skipStatusCheck: action === 'new',
      });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // 인증권 부족 → OV 닫고 구매 팝업 열기 → 구매 팝업 닫히면 OV 다시 열기
  useEffect(() => {
    if (ov.step !== STEP.PURCHASE) return;
    const currentSaleUuid = saleUuid;
    onClose?.();
    window.openTicketPurchase?.(
      { serviceName: OV_SERVICE_NAME, saleUuid: currentSaleUuid },
      () => {
        window.openOwnerVerification?.({ saleUuid: currentSaleUuid });
      },
    );
  }, [ov.step]);

  const handleClose = () => {
    const result = ov.handleCloseAttempt();
    if (result === 'close') onClose?.();
  };

  const handleExitConfirm = () => {
    const result = ov.handleExitConfirm();
    if (result === 'close') onClose?.();
  };

  const handleSend = async (recipientData, files) => {
    const result = await ov.handleSend(recipientData, files);
    if (result?.status === 'success') {
      onComplete?.(result);
      try {
        const statusData = await ovApi.getVerificationStatus(saleUuid);
        if (statusData.verification_status) {
          window.updateOvStatus?.(saleUuid, statusData.verification_status);
        }
      } catch (e) {
        console.error('인증 상태 업데이트 에러:', e);
      }
      toast.success('인증 신청이 완료되었습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
      ov.startVerification(saleUuid);
    }
  };


  const getExpiresAt = () => addDays(today(), 7);

  const handleResend = () => {
    const vUuid = ov.verificationData?.uuid || ov.draftUuid;
    if (!vUuid) return;
    ovApi.resendLink(vUuid)
      .then((res) => {
        toast.success('인증 메시지가 재전송되었습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
        ov.setVerificationData((prev) => prev ? {
          ...prev,
          link_remaining_sends: res.link_remaining_sends ?? Math.max(0, (prev.link_remaining_sends || 0) - 1),
        } : prev);
      })
      .catch((err) => {
        toast.error(
          err.body?.reason === 'send_limit_exceeded'
            ? '메세지 재전송 가능한 횟수를 초과했습니다'
            : '재전송에 실패했습니다.',
          { position: TOAST_POSITION.BOTTOM_CENTER }
        );
      });
  };

  const handleDirectShare = () => {
    const linkUrl = ov.verificationData?.link_url;
    if (linkUrl) {
      setDirectShareLink(linkUrl);
      setDirectShareOpen(true);
    } else {
      toast.error('링크가 아직 생성되지 않았습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
    }
  };

  if (ov.step === STEP.LOADING || ov.step === STEP.PURCHASE) return null;

  return (
    <>
      {ov.step === STEP.MEMBERSHIP_REQUIRED && (
        <MembershipRequiredModal
          onConfirm={() => {
            onClose?.();
            window.openMembershipInfoPage?.();
          }}
          onClose={onClose}
        />
      )}

      {ov.step === STEP.CONFIRM && (
        <OwnerVerificationConfirmModal
          isOpen
          estate={ov.estate || {}}
          ticketCount={ov.ticketCount}
          onStart={ov.handleStart}
          onCharge={ov.handleCharge}
          onClose={onClose}
        />
      )}

      {ov.step === STEP.DETAIL && (
        <EstateDetailStep
          estate={ov.estate || {}}
          onNext={ov.handleDetailNext}
          onClose={handleClose}
        />
      )}

      {ov.step === STEP.NOTICE && (
        <NoticeStep
          ticketQuantity={ov.ticketQuantity}
          onNext={ov.handleNoticeNext}
          onClose={handleClose}
        />
      )}

      {ov.step === STEP.RECIPIENT && (
        <RecipientInfoStep
          draftUuid={ov.draftUuid}
          expiresAt={getExpiresAt()}
          onSend={handleSend}
          onClose={handleClose}
          loading={ov.loading}
        />
      )}

      <DirectShareModal
        isOpen={directShareOpen}
        linkUrl={directShareLink}
        officeName={window.loggedInUser?.office_name}
        onClose={() => { setDirectShareOpen(false); setDirectShareLink(''); }}
      />


      {ov.showExitConfirm && (
        <ExitConfirmModal
          onConfirm={handleExitConfirm}
          onBack={ov.handleExitBack}
        />
      )}

      {(ov.step === STEP.IN_REVIEW || ov.step === STEP.COMPLETED ||
        ov.step === STEP.EXPIRED || ov.step === STEP.FAILED || ov.step === STEP.REJECTED) && (
        <VerificationStatusModal
          status={ov.step}
          verification={ov.verificationData || {}}
          attachments={ov.verificationData?.attachments || []}
          reason={ov.verificationData?.reason}
          detail={ov.verificationData?.detail}
          linkUrl={ov.verificationData?.link_url}
          remainingSends={ov.verificationData?.link_remaining_sends ?? 0}
          onClose={onClose}
          onResend={handleResend}
          onDirectShare={handleDirectShare}
          onDispute={async (data) => {
            await ovApi.submitDispute(data);
            toast.success('이의신청이 접수되었습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
            onClose?.();
          }}
          disputeUuid={ov.verificationData?.uuid || ov.draftUuid}

          onRenew={() => {
            if (saleUuid) ov.startVerification(saleUuid, { skipStatusCheck: true });
          }}
          onResubmit={async (files) => {
            if (!files?.length || !ov.draftUuid) return;
            for (const file of files) {
              try {
                const saveName = await uploadFileToAurora('ov_attachment', file, ov.draftUuid);
                if (saveName) {
                  await ovApi.registerAttachment(ov.draftUuid, {
                    saveName,
                    originalFilename: file.name,
                    fileSize: file.size,
                    mimeType: file.type || 'application/octet-stream',
                  });
                }
              } catch (e) {
                console.error('보완 서류 업로드 에러:', e);
                toast.error('일부 파일 업로드에 실패했습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
              }
            }
            toast.success('보완 서류가 제출되었습니다.', { position: TOAST_POSITION.BOTTOM_CENTER });
            onClose?.();
          }}
        />
      )}
    </>
  );
}

export default OwnerVerificationWidget;
